import { ASSESSMENT_LEAD_ENDPOINT, ASSESSMENT_LEAD_FORM_TYPE, ASSESSMENT_LEAD_SOURCE } from '../../config/leadCaptureConfig'
import type { AssessmentState, LeadPayload } from '../assessment/types'
import { validateLeadPayload } from '../assessment/validation'

export type LeadSubmitResult =
  | { ok: true; leadId?: string }
  | { ok: false; error: string; configured: boolean }

export type AssessmentLeadExtras = {
  answers?: AssessmentState | Record<string, unknown>
  results?: unknown
  pdfBase64?: string
  pdfFileName?: string
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  const chunk = 0x8000
  let binary = ''
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

export { blobToBase64 }

export async function submitAssessmentLead(
  payload: Omit<LeadPayload, 'source' | 'formType' | 'page'> & { page?: string },
  extras: AssessmentLeadExtras = {},
): Promise<LeadSubmitResult> {
  const body: LeadPayload = {
    ...payload,
    source: ASSESSMENT_LEAD_SOURCE,
    formType: ASSESSMENT_LEAD_FORM_TYPE,
    page: payload.page || '/tools/uae-sustainability-assessment/results',
  }

  const errors = validateLeadPayload(body)
  if (errors.length) {
    return { ok: false, error: errors[0], configured: true }
  }

  try {
    const res = await fetch(ASSESSMENT_LEAD_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: body.workEmail,
        workEmail: body.workEmail,
        company: body.companyName,
        companyName: body.companyName,
        fullName: body.fullName || '',
        jobTitle: body.jobTitle || '',
        phone: body.phone || '',
        preferredContact: body.preferredContact || '',
        interestInDemo: body.interestInDemo ? 'yes' : 'no',
        actingAsConsultant: body.actingAsConsultant ? 'yes' : 'no',
        consentReport: body.consentReport ? 'yes' : 'no',
        consentMarketing: body.consentMarketing ? 'yes' : 'no',
        qualificationSummary: body.qualificationSummary || '',
        methodologyVersion: body.methodologyVersion || '',
        source: body.source,
        formType: body.formType,
        page: body.page,
        answers: extras.answers ?? null,
        results: extras.results ?? null,
        pdfBase64: extras.pdfBase64 || null,
        pdfFileName: extras.pdfFileName || null,
      }),
    })

    const data = (await res.json().catch(() => ({}))) as {
      error?: string
      ok?: boolean
      leadId?: string
    }

    if (!res.ok) {
      const serverError = data.error || ''
      if (res.status === 503 || /not configured/i.test(serverError)) {
        return {
          ok: false,
          error:
            serverError ||
            'Lead storage is not configured. Set SUPABASE_SECRET_KEY (and optionally Resend) on the Worker, then redeploy.',
          configured: false,
        }
      }
      return {
        ok: false,
        error: serverError || 'We could not submit your details. Please check the form and try again.',
        configured: true,
      }
    }

    return { ok: true, leadId: data.leadId }
  } catch {
    return {
      ok: false,
      error: 'Network error submitting your details. Please try again.',
      configured: true,
    }
  }
}
